/**
 * The Panel's sections, below 992px (SPEC_NAVEGACION_MOVIL, "Panel").
 *
 * The spec groups a FixyBee's sections as TRABAJO / COBROS / NEGOCIO and gives
 * the client a single list (Resumen · Pagos · Ajustes). Every destination is a
 * route that already exists; the Panel index itself is the one new address,
 * because the spec makes "Resumen" a row INSIDE the Panel — so the Panel cannot
 * also be the overview it links to.
 *
 * **Two rows the spec does not list, added rather than dropped** — flagged in
 * the T12 report. `requests-for-you` and `requests` are the only way into R6's
 * directed requests and DY2.6's own-posted jobs, neither of them a tab. Porting
 * the spec's list literally would have left both unreachable on a phone, and
 * R6 exists precisely because nothing on screen said a request was waiting.
 *
 * FixyTerminal is absent by instruction (montage brief §0.5), not by omission.
 */

export function panelGroupsFor({ role, stats, directedPending, verified, methods }) {
  if (role === "provider") {
    return [
      {
        key: "work",
        eyebrowKey: "panel.groups.work",
        rows: [
          { key: "overview", to: "/provider/dashboard", icon: "layout-dashboard", labelKey: "dashLayout.overview" },
          { key: "my-quotes", to: "/provider/quotes", icon: "file-text", labelKey: "dashLayout.myQuotes", count: stats?.quotesOpen },
          { key: "requests-for-you", to: "/provider/requests-for-you", icon: "inbox", labelKey: "dashLayout.requestsForYou", count: directedPending },
          { key: "my-requests", to: "/provider/requests", icon: "briefcase", labelKey: "dashLayout.myRequests" },
          { key: "verification", to: "/provider/verification", icon: "shield-check", labelKey: "dashLayout.verification", pill: verified ? "verified" : null },
        ],
      },
      {
        // COBROS. The connected handles, each with its own row, then the way
        // into the screen that manages them — the mockup's compact list.
        //
        // The group stays even with nothing connected, which today is every
        // FixyBee: production had zero payment links when this shipped, so a
        // group that hid itself when empty would hide itself from all 18, and
        // the "Administrar" row is the only path from the Panel to the screen.
        // An eyebrow over a single row is a smaller cost than a feature nobody
        // can find.
        key: "payouts",
        eyebrowKey: "panel.groups.payouts",
        rows: [
          ...(methods || []).map((m) => ({
            key: `payout-${m.app_key}`,
            to: "/panel/payment-methods",
            icon: "wallet",
            label: m.label,
            mono: m.handle,
          })),
          {
            key: "manage-payouts",
            to: "/panel/payment-methods",
            icon: "plus",
            labelKey: "payouts.manage",
            accent: true,
          },
        ],
      },
      {
        key: "business",
        eyebrowKey: "panel.groups.business",
        rows: [
          { key: "billing", to: "/provider/billing", icon: "credit-card", labelKey: "dashLayout.billing" },
          { key: "web-page-editor", to: "/provider/landing-editor", icon: "globe", labelKey: "dashLayout.webPageEditor" },
          { key: "user-guide", action: "guide", icon: "book-open", labelKey: "dashLayout.userGuide" },
          { key: "account-settings", to: "/provider/settings", icon: "settings", labelKey: "dashLayout.accountSettings" },
        ],
      },
    ];
  }
  // Client. The spec's list is Resumen · Pagos · Ajustes; "Pagos" names a page
  // this app does not have (no /client/payments route, no endpoint behind it),
  // so it is reported rather than invented. Messages and My jobs are tabs.
  return [
    {
      key: "client",
      eyebrowKey: null,
      rows: [
        { key: "overview", to: "/client/dashboard", icon: "layout-dashboard", labelKey: "dashLayout.overview" },
        { key: "my-jobs", to: "/client/jobs", icon: "briefcase", labelKey: "dashLayout.myJobs" },
        { key: "user-guide", action: "guide", icon: "book-open", labelKey: "dashLayout.userGuide" },
        { key: "account-settings", to: "/client/settings", icon: "settings", labelKey: "dashLayout.accountSettings" },
      ],
    },
  ];
}

/**
 * The title a dashboard section shows next to "‹ Panel", found by route.
 * Returns null for a page that is not a Panel section.
 *
 * **Two of these routes are also bottom-bar tabs** — `/provider/quotes` is
 * Cotizaciones and `/client/jobs` is Trabajos — and the spec asks for two
 * different things about them: the Panel section shows "‹ Panel" with Menú
 * active (screen 10), while the tab must light its own cell. Both are right;
 * they describe the same page reached two ways. So the header is shown only
 * when the visitor ARRIVED from the Panel, which the Panel says in router
 * state. On a reload that state is gone and the page behaves as the tab, which
 * is the honest default: nothing on screen came from the Panel any more.
 */
const TITLES = {
  "/panel/payment-methods": "payouts.title",
  "/provider/dashboard": "dashLayout.overview",
  "/provider/quotes": "dashLayout.myQuotes",
  "/provider/requests-for-you": "dashLayout.requestsForYou",
  "/provider/requests": "dashLayout.myRequests",
  "/provider/verification": "dashLayout.verification",
  "/provider/billing": "dashLayout.billing",
  "/provider/landing-editor": "dashLayout.webPageEditor",
  "/provider/settings": "dashLayout.accountSettings",
  "/client/dashboard": "dashLayout.overview",
  "/client/jobs": "dashLayout.myJobs",
  "/client/settings": "dashLayout.accountSettings",
};

export function sectionTitleKey(pathname) {
  return TITLES[String(pathname || "")] || null;
}

export default panelGroupsFor;
